import type { MergeableStore as SchemaMergeableStore } from 'tinybase/with-schemas';

import { WebSocket as ReconnectingWebSocket } from 'partysocket';
import { createMergeableStore, createQueries, MergeableStore } from 'tinybase';
import { createWsSynchronizer } from 'tinybase/synchronizers/synchronizer-ws-client';

import { isE2eTestMode, isProd } from '~/client/config/appConfig';
import { trackError } from '~/client/lib/appInsights';
import { globalStoreTablesSchema, GlobalStoreId } from '~/common/tinybase/globalStoreSchema';
import { userStoreTablesSchema, userStoreValuesSchema } from '~/common/tinybase/userStoreSchema';
import { GlobalStoreSchemas, UserStoreSchemas } from '~/common/types/tinybase';

import type { UpdateInitialSyncProgressFn } from './tinybaseStore';

import { ClientGlobalStore } from './ClientGlobalStore';
import { ClientUserStore } from './ClientUserStore';
import { initCustomIndexedDbPersister } from './persister/customIndexedDbPersister';
import { clearOutdatedPersistedData } from './persister/helpers';
import { StoreCrypto } from './StoreCrypto';

// Bounds how long the client waits for the server's response to a pull request.
// Use a high value to accommodate pulling large datasets over slow connections.
// Use a lower value in E2E tests to fail faster when the server doesn't respond
// as expected as a slow mobile network won't be a factor
export const wsReqTimeoutSecs = isE2eTestMode ? 30 : 60;

/** Using a relative url is not supported in some older browsers **/
const getWsUrl = (storePath: string) => {
  const { protocol, host } = window.location;
  const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${host}/sync/${storePath}`;
};

const createWsSync = (storePath: string, store: MergeableStore) => {
  // this is a WebSocket api-compatible reconnecting implementation.
  // the cast is needed due to some subtle .d.ts typing differences
  const webSocket = new ReconnectingWebSocket(getWsUrl(storePath));

  return createWsSynchronizer(
    store,
    webSocket as WebSocket,
    wsReqTimeoutSecs,
    (...args) => !isProd && console.debug(`[${storePath}] SEND:`, ...args),
    (...args) => !isProd && console.debug(`[${storePath}] RECEIVE:`, ...args),
    (err) => {
      console.warn(`[${storePath}] Ignored sync error:`, err);
      trackError(err, { storePath, syncError: true });
    },
  );
};

export class ClientSyncStores {
  private constructor(
    public readonly globalStore: ClientGlobalStore,
    public readonly userStore: ClientUserStore,
    private readonly userStoreCrypto: StoreCrypto,
    private readonly updateInitialSyncProgress: UpdateInitialSyncProgressFn,
  ) {}

  public static async create(
    userId: string,
    userStoreCrypto: StoreCrypto,
    updateInitialSyncProgress: UpdateInitialSyncProgressFn,
  ) {
    const globalStore = createMergeableStore().setTablesSchema(globalStoreTablesSchema);
    const userStore = createMergeableStore()
      .setTablesSchema(userStoreTablesSchema)
      .setValuesSchema(userStoreValuesSchema);

    await clearOutdatedPersistedData();

    const [globalPersister, userPersister] = await Promise.all([
      initCustomIndexedDbPersister(globalStore, 'tinybase-global').finally(() =>
        updateInitialSyncProgress('globalStoreInit'),
      ),
      initCustomIndexedDbPersister(userStore, 'tinybase-user', userStoreCrypto).finally(() =>
        updateInitialSyncProgress('userStoreInit'),
      ),
    ]);

    const syncStores = new ClientSyncStores(
      new ClientGlobalStore(
        globalStore as unknown as SchemaMergeableStore<GlobalStoreSchemas>,
        globalPersister,
        await createWsSync(GlobalStoreId, globalStore),
        createQueries(globalStore),
      ),
      new ClientUserStore(
        userId,
        userStore as unknown as SchemaMergeableStore<UserStoreSchemas>,
        userPersister,
        await createWsSync(userId, userStore),
        createQueries(userStore),
      ),
      userStoreCrypto,
      updateInitialSyncProgress,
    );

    if (!isProd) {
      window.syncStores = syncStores;
    }

    return syncStores;
  }

  public async startSync() {
    console.log('start sync');
    await this.globalStore.startAutoLoad(); // Global data is read-only
    await this.userStore.startSync();
    this.updateInitialSyncProgress('syncStarted');
    console.log('sync started');

    // Manually save persisters to ensure any changes in table hashes are saved even if no row changes were detected
    // It seems that row hashes are deterministic but table hashes aren't always
    // (could be related to the order in which the rows are added)
    await this.globalStore.persister?.save();
    await this.userStore.persister?.save();
  }

  public async destroy() {
    // Purge user store data
    this.userStore.setInactive();
    await this.userStore.purgeData();
    await this.userStoreCrypto.deleteStoredParams();

    this.globalStore.destroy();
    this.userStore.destroy();
  }
}
